import { useEffect, useState } from "react";
import { Crown, Trash2, Plus, X, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../api";
import { useUnit } from "../context/UnitContext";

const TeamDuelCard = ({ d, unit, dw, onDelete }) => {
  const total = d.team_a.volume + d.team_b.volume;
  const pct = total > 0 ? (d.team_a.volume / total) * 100 : 50;
  return (
    <div data-testid={`team-duel-${d.id}`}
      className={`bg-[#14141A] border p-3 ${d.finished ? "border-[#2E2E3A]" : "border-[#FF3B30]/30"}`}>
      <div className="flex items-center justify-between">
        <p className="text-white text-sm font-semibold">{d.exercice} <span className="text-[#B0B0B4] text-[10px] uppercase">· 2v2</span></p>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${
            d.finished ? "bg-white/10 text-[#B0B0B4]" : "bg-[#FF3B30]/15 text-[#FF3B30]"}`}>
            {d.finished ? "Terminé" : `${d.days_left} j restants`}
          </span>
          {d.can_delete && (
            <button data-testid={`team-duel-delete-${d.id}`} onClick={() => onDelete(d.id)}
              className="text-[#B0B0B4] hover:text-[#FF3B30] transition-colors" aria-label="Supprimer">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        {[["a", d.team_a], ["b", d.team_b]].map(([key, t], i) => (
          <div key={key} className={i === 1 ? "text-right" : ""}>
            <p className={`font-semibold flex items-center gap-1 ${i === 1 ? "justify-end" : ""} ${d.my_team === key ? "text-[#FF3B30]" : "text-white"}`}>
              {d.finished && d.winner_team === key && <Crown size={13} className="text-amber-400" />}
              {t.names.join(" + ")}
            </p>
            <p className="text-[#ccc] font-mono">{Math.round(dw(t.volume))} {unit} vol.</p>
          </div>
        ))}
      </div>
      <div className="mt-2 h-[5px] bg-[#14141A] flex overflow-hidden">
        <div className="bg-[#FF3B30] transition-all" style={{ width: `${pct}%` }} />
        <div className="bg-[#ccc] flex-1" />
      </div>
      {d.finished && (
        <p data-testid={`team-duel-winner-${d.id}`} className="mt-2 text-xs flex items-center gap-1.5">
          {!d.winner_team
            ? <span className="text-[#B0B0B4]">Égalité parfaite entre les deux équipes !</span>
            : <><Crown size={13} className="text-amber-400" />
              <span className="text-amber-400 font-semibold">
                {(d.winner_team === "a" ? d.team_a : d.team_b).names.join(" + ")} {d.my_team === d.winner_team ? "— vous gagnez le 2v2 !" : "gagnent le 2v2 !"}
              </span></>}
        </p>
      )}
    </div>
  );
};

export const TeamDuelSection = ({ members, exercices }) => {
  const { unit, dw } = useUnit();
  const [teamDuels, setTeamDuels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [team, setTeam] = useState({ teammate_id: "", opponent1_id: "", opponent2_id: "", exercice: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/team-duels").then(({ data }) => setTeamDuels(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/team-duels", team);
      toast.success("Duel d'équipe lancé ! 2 contre 2 🥊");
      setShowForm(false);
      setTeam({ teammate_id: "", opponent1_id: "", opponent2_id: "", exercice: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur lors de la création du 2v2");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    await api.delete(`/team-duels/${id}`);
    load();
  };

  const ready = team.teammate_id && team.opponent1_id && team.opponent2_id && team.exercice &&
    new Set([team.teammate_id, team.opponent1_id, team.opponent2_id]).size === 3;

  return (
    <>
      <div className="flex items-center justify-between mt-5 mb-2">
        <h3 className="font-display text-xl text-white flex items-center gap-2">
          DUELS D'ÉQUIPE 2V2 <Users size={16} className="text-[#FF3B30]" />
        </h3>
        <button data-testid="team-duel-new-button" onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-xs uppercase tracking-wider border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 px-2.5 py-1.5 transition-colors">
          {showForm ? <X size={13} /> : <Plus size={13} />} {showForm ? "Annuler" : "Lancer un 2v2"}
        </button>
      </div>

      {showForm && (members.length < 3 ? (
        <p data-testid="team-duel-not-enough" className="bg-[#14141A] border border-[#2E2E3A] p-3 text-xs text-[#B0B0B4] mb-3">
          Il faut au moins 4 membres pour un 2v2 — invite tes amis depuis l'admin !
        </p>
      ) : (
        <div data-testid="team-duel-form" className="bg-[#14141A] border border-[#FF3B30]/40 p-3 mb-3 space-y-2">
          <p className="text-xs text-[#B0B0B4]">2 contre 2 pendant 7 jours · les volumes des coéquipiers s'additionnent</p>
          {[["teammate_id", "Ton coéquipier…"], ["opponent1_id", "Adversaire 1…"], ["opponent2_id", "Adversaire 2…"]].map(([k, ph]) => (
            <select key={k} data-testid={`team-duel-${k}-select`} value={team[k]}
              onChange={(e) => setTeam({ ...team, [k]: e.target.value })} className="set-input w-full">
              <option value="">{ph}</option>
              {members.filter((m) => m.id === team[k] || !Object.values(team).includes(m.id))
                .map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          ))}
          <select data-testid="team-duel-exercice-select" value={team.exercice}
            onChange={(e) => setTeam({ ...team, exercice: e.target.value })} className="set-input w-full">
            <option value="">Choisis l'exercice…</option>
            {exercices.map((ex) => <option key={ex} value={ex}>{ex}</option>)}
          </select>
          <button data-testid="team-duel-create-button" onClick={create} disabled={!ready || saving}
            className="w-full h-10 bg-[#FF3B30] text-white font-display text-lg tracking-wide hover:bg-[#e0332a] transition-colors disabled:opacity-30">
            LANCER LE 2V2
          </button>
        </div>
      ))}

      {teamDuels.length === 0 && !showForm && (
        <p className="bg-[#14141A] border border-[#2E2E3A] p-3 text-xs text-[#B0B0B4]">
          Aucun duel d'équipe. Fais équipe avec un ami et défiez deux autres membres !
        </p>
      )}

      <div className="space-y-2">
        {teamDuels.map((d) => (
          <TeamDuelCard key={d.id} d={d} unit={unit} dw={dw} onDelete={remove} />
        ))}
      </div>
    </>
  );
};
